import { Component, Input, OnInit } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  BehaviorSubject,
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';
import {
  SafetyModel,
  SafetyListResponse,
  LicenseTypeResponseModel,
} from '../../../models/event.model';
import { AppCommon } from '../../../providers/app-common';
import { EventViewModel } from '../../../models-views/event.view.model';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-cet-compliance-safety',
  standalone: false,
  templateUrl: './cet-compliance-safety.component.html',
  styleUrls: ['./cet-compliance-safety.component.scss'],
})
export class CetComplianceSafetyComponent implements OnInit {
  @Input() eventId: any = null;
  @Input() eventData: any = null;
  @Input() sourcingNeedRecordId: number | null = null;

  safetyListResponse = new BehaviorSubject<SafetyListResponse>(
    new SafetyListResponse()
  );

  enablePermissions: boolean = false;
  loading = false;
  searchValue = '';
  page_size = 100;
  page_number = 1;
  totalCount = 0;
  complianceList: any = [];
  isVisibleModal = false;
  isEditing = false;
  currentEditId: number | null = null;
  selectedPermission: any = null;
  is_compliance_safety_required: boolean = false;
  form: FormGroup;
  searchValue$ = new Subject<string>();

  eventForm: FormGroup;
  loadingConversion = false;
  currencies = [];
  destroy$ = new Subject();
  loggedInUser: any = '';
  lienceList: any = [];

  constructor(
    private vm: EventViewModel,
    private fb: FormBuilder,
    private modalService: NzModalService
  ) {
    this.vm.parentSafetyComponent = this;
    this.loggedInUser = AppCommon.getloggedInObject().user;
    this.vm.currencyList
      .pipe(takeUntil(this.destroy$))
      .subscribe((d) => (this.currencies = d.results));

    this.eventForm = this.fb.group({
      compliance_safety: [null, Validators.required],
      budget_currency_type: [null, Validators.required],
      budget_currency_amount: [0, Validators.required],
      usd_currency_exchange_rate: [0, Validators.required],
      budget_amount: [{ value: 0, disabled: true }],
    });

    this.eventForm
      .get('budget_currency_amount')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => this.calculateAmount(val));

    this.eventForm
      .get('usd_currency_exchange_rate')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((rate) =>
        this.calculateAmount(this.eventForm.get('budget_currency_amount').value)
      );

    // search debounce
    this.searchValue$.pipe(debounceTime(500)).subscribe(() => {
      this.page_number = 1;
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.vm.subscribeToSafetyResponse(this.vm.safetyListResponse);
    this.vm.safetyListResponse.subscribe((res) => {
      if (res) {
        this.complianceList = res.results;
        this.totalCount = res.count;
        this.loading = false;
      }
    });

    this.vm.getLicenseType();
    this.vm.getCurrencyList(1, 100);

    if (this.eventData) {
      try {
        if (
          this.eventData.is_compliance_safety_required != null &&
          this.eventData.is_compliance_safety_required !== undefined
        ) {
          this.enablePermissions = this.eventData.is_compliance_safety_required;
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  getCurrenyName(id: number) {
    return id &&
      this.currencies != null &&
      this.currencies != undefined &&
      this.currencies.length > 0
      ? this.currencies.filter((v) => v.id === id)[0]?.symbol
      : 'USD';
  }

  // ------------------ Yes/No Toggle ------------------
  onToggleSafety(value: boolean) {
    let payload: any = {};
    payload.id = this.eventId;
    payload.modified_by = this.loggedInUser.id;
    payload.is_compliance_safety_required = value;
    payload.sourcing_need =
    this.sourcingNeedRecordId ?? this.eventData?.sourcing_need ?? null;
    this.vm.updateEventById(payload, 'compliance-safety');
    this.enablePermissions = value;
    if (value) this.loadData();
  }

  // ------------------ Load Data ------------------
  loadData() {
    if (!this.enablePermissions) return;
    this.loading = true;
    this.vm.getSafetyList(
      this.searchValue,
      this.page_size,
      this.page_number,
      this.eventId
    );
  }

  // ------------------ Search ------------------
  onSearch() {
    this.page_number = 1;
    this.searchValue$.next(this.searchValue);
  }

  // ------------------ Add / Edit Modal ------------------
  onAdd() {
    this.isVisibleModal = true;
    this.isEditing = false;
    this.currentEditId = null;
    this.selectedPermission = null;
    this.eventForm.reset();
  }

  onEdit(record: any) {
    this.isVisibleModal = true;
    this.isEditing = true;
    this.currentEditId = record.id!;
    this.selectedPermission = record;

    this.eventForm.patchValue({
      // compliance_safety: record.compliance_safety,
      compliance_safety: record.license,
      budget_currency_type: record.currency_type,
      budget_currency_amount: parseFloat(record.currency_amount) || 0,
      usd_currency_exchange_rate: parseFloat(record.exchange_rate) || 0,
      budget_amount: this.truncate(parseFloat(record.amount_usd) || 0, 2),
    });
  }

  handleModalCancel() {
    this.isVisibleModal = false;
    this.eventForm.reset();
    this.currentEditId = null;
    this.selectedPermission = null;
  }

  onSubmit() {
    if (this.eventForm.invalid) return;

    const formValues = this.eventForm.getRawValue(); // includes disabled field

    const payload: any = {
      // compliance_safety: formValues.compliance_safety,
      license: formValues.compliance_safety,
      currency_type: formValues.budget_currency_type,
      currency_amount: formValues.budget_currency_amount?.toString() || '0',
      exchange_rate: formValues.usd_currency_exchange_rate?.toString() || '0',
      amount_usd: formValues.budget_amount?.toString() || '0',
      event: Number(this.eventId),
    };

    if (this.isEditing) {
      payload.created_by = this.selectedPermission?.created_by || 0;
      payload.modified_by = this.loggedInUser?.id || 0;
      this.vm.updateSafety(this.currentEditId, payload);
    } else {
      payload.created_by = this.loggedInUser?.id || 0;
      this.vm.createSafety(payload);
    }

    this.handleModalCancel();
  }

  // ------------------ Delete ------------------
  deletePermission(record: any) {
    // this.modalService.confirm({
    //   nzTitle: `Are you sure delete "${record.compliance_safety_name}"?`,
    //   nzContent: '<b style="color: red;">This cannot be undone.</b>',
    //   nzOkText: 'Yes',
    //   nzOkType: 'primary',
    //   nzOkDanger: true,
    //   nzOnOk: () => this.vm.deleteSafety(record.id!),
    //   nzCancelText: 'No',
    // });
    this.vm.deleteSafety(record.id!)
  }

  // ------------------ Pagination ------------------
  onPageChange(page: number) {
    this.page_number = page;
    this.loadData();
  }

  onPageSizeChange(size: number) {
    this.page_size = size;
    this.page_number = 1;
    this.loadData();
  }

  // ------------------ Callbacks from ViewModel ------------------
  dataReceived() {
    this.loading = false;
  }

  safetyCreated() {
    this.handleModalCancel();
    this.loadData();
  }

  safetyUpdated() {
    this.handleModalCancel();
    this.loadData();
  }

  safetyDeleted() {
    this.loadData();
  }

  // Called when currency changes
  onCurrencyChange(currencyId: any) {
    if (!currencyId) return;
    this.loadingConversion = true;
    this.vm.getconvertedCurrency(currencyId, '2', 'compliance-safety');
  }

  convertedCurrencyRecieved(e: any) {
    if (e.rate) {
      this.eventForm
        .get('usd_currency_exchange_rate')
        .setValue(e.rate.toFixed(4));

      if (this.eventForm.get('budget_currency_amount').value) {
        this.eventForm
          .get('budget_amount')
          .setValue(
            this.truncate(
              this.eventForm.get('budget_currency_amount').value * e.rate,
              2
            )
          );
      }

      this.loadingConversion = false;
    }
  }

  calculateAmount(amount: number) {
    if (amount && this.eventForm.get('usd_currency_exchange_rate').value) {
      let result =
        amount * this.eventForm.get('usd_currency_exchange_rate').value;
      this.eventForm.get('budget_amount').setValue(this.truncate(result, 2));
    } else {
      this.eventForm.get('budget_amount').setValue(0);
    }
  }

  truncate(number: any, decimals: any): any {
    const powerOfTen = Math.pow(10, decimals);
    return Math.floor(number * powerOfTen) / powerOfTen;
  }

  // Fetch latest exchange conversion
  fetchConvertion(e: any) {
    e.preventDefault();
    const currencyId = this.eventForm.get('budget_currency_type')?.value;
    if (currencyId) {
      this.loadingConversion = true;
      this.vm.getconvertedCurrency(currencyId, '2', 'compliance-safety');
    }
  }

  liscenceRecived() {
    this.lienceList = this.vm.licenseTypeResponseModel.value.results;
  }

  onClearSearch() {
    this.searchValue = '';
    this.vm.getSafetyList(
      this.searchValue,
      this.page_size,
      this.page_number,
      this.eventId
    );
  }
}
